import styled from '@emotion/styled';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';

import { authenticationService, navigationService } from 'src/config/rootService';
import { Dispatch } from 'src/config/rootStore';
import ssoVerificationImg from 'src/public/img/sso-verification.svg';
import { Button } from 'src/shared/components/Button';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';
import { color, font, mediaQueries } from 'src/shared/styles/variables';
import { isAuthenticated } from '../selectors/authenticationSelectors';

const Wrapper = styled.div({
    width: '100vw',
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    backgroundColor: color.white,
});

const InnerContainer = styled.div({
    position: 'relative',
    padding: '70px 40px',
    backgroundColor: color.white,
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    [mediaQueries.sm]: {
        padding: '30px 15px',
    },
});

const Image = styled.img({
    display: 'block',
    margin: '0 auto',
});

const MobileContentWrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
});

const Title = styled.p(
    {
        width: '243px',
        fontWeight: 800,
        marginTop: 20,
        marginBottom: 40,
        textAlign: 'center',
        color: color.pickledBluewood,
        fontsize: '28px',
        lineHeight: '32px',
    },
    font.heavy(font.size.mediumExtended),
);

interface MatchParams {
    token: string;
}

type Props = RouteComponentProps<MatchParams>;

const SingleSignOnVerify: React.FC<Props> = ({ match }) => {
    const authenticated = useSelector(isAuthenticated);
    const dispatch = useDispatch<Dispatch>();
    const flags = useFlags();
    const migrateToBranchio = flags?.migrateToBranchio;
    const { t } = useTranslation();

    const { token } = match.params;

    const mobileBrowserDetected = /Mobi|Android/i.test(navigator.userAgent);

    const handleError = () => {
        if (!authenticated) {
            navigationService.navigate('/sso-failed');
        }
    };

    const continueToApp = () => {
        // In case the token was reissued, redirect using the new token
        const authData = authenticationService.authData;

        if (authData) {
            let appLoginUrl;
            const newToken = authData.refresh_token;
            if (!migrateToBranchio) {
                appLoginUrl = `modernhealth://token-login/${newToken}`;
            } else {
                appLoginUrl = `https://modernhealth.app.link/token-login?token=${newToken}`;
            }

            window.location.replace(appLoginUrl);
        }
    };

    const continueToMH = () => {
        if (mobileBrowserDetected) {
            continueToApp();
        } else {
            navigationService.navigate('/authenticated');
        }
    };

    useEffect(() => {
        if (!token) {
            handleError();
        }

        authenticationService
            .refreshByToken(token)
            .then(authData => dispatch.authentication.authenticate(authData))
            .catch(handleError);

        setTimeout(continueToMH, 5000);
    }, []);

    return (
        <Wrapper>
            <InnerContainer>
                {!authenticated ? (
                    <CenteredSpinner />
                ) : (
                    <MobileContentWrapper>
                        <Image src={ssoVerificationImg} />
                        <Title>{t('auth:single_sign_on_form.authentication_successful')}</Title>

                        <Button onClick={continueToMH}>
                            {t('auth:single_sign_on_form.continue_to_modern_health')}
                        </Button>
                    </MobileContentWrapper>
                )}
            </InnerContainer>
        </Wrapper>
    );
};

export default SingleSignOnVerify;
